import {
    Asset,
    BLEND_NORMAL,
    BLEND_MULTIPLICATIVE,
    BoundingBox,
    Color,
    Entity,
    Mat4,
    Quat,
    SHADERLANGUAGE_GLSL,
    Vec3,
    MeshInstance,
    StandardMaterial,
    Texture
} from 'playcanvas';

import { Element, ElementType } from './element';
import { calcInvRotationMat3, defaultGlobalClipBox, type GlobalClipBox } from './global-clip-box';
import { Serializer } from './serializer';
import { Transform } from './transform';
import { VideoTextureSource, type VideoTexturePlaybackMode } from './video-texture';

const ASSET_REFCOUNT_KEY = '__nsite_mesh_asset_refCount';

const vec = new Vec3();
const veca = new Vec3();
const vecb = new Vec3();
const billboardLocalCamera = new Vec3();
const billboardInverseWorld = new Mat4();

const clipBoxChunk = `
uniform float clipBoxEnabled;
uniform vec3 clipBoxCenter;
uniform vec3 clipBoxHalf;
uniform mat3 clipBoxInvRot;

void clipBoxDiscard(vec3 worldPos) {
    if (clipBoxEnabled <= 0.5) {
        return;
    }
    vec3 pLocal = clipBoxInvRot * (worldPos - clipBoxCenter);
    if (abs(pLocal.x) > clipBoxHalf.x || abs(pLocal.y) > clipBoxHalf.y || abs(pLocal.z) > clipBoxHalf.z) {
        discard;
    }
}
`;

const boundingPoints =
    [-1, 1].map((x) => {
        return [-1, 1].map((y) => {
            return [-1, 1].map((z) => {
                return [
                    new Vec3(x, y, z), new Vec3(x * 0.75, y, z),
                    new Vec3(x, y, z), new Vec3(x, y * 0.75, z),
                    new Vec3(x, y, z), new Vec3(x, y, z * 0.75)
                ];
            });
        });
    }).flat(3);

type TextureApplicationResult = {
    readyMeshInstanceCount: number;
    boundMeshInstanceCount: number;
};

class Mesh extends Element {
    asset: Asset;
    entity: Entity;
    renderEntity: Entity;
    numMeshInstances = 0;
    changedCounter = 0;

    selectionBoundStorage: BoundingBox;
    localBoundStorage: BoundingBox;
    worldBoundStorage: BoundingBox;
    selectionBoundDirty = true;
    localBoundDirty = true;
    worldBoundDirty = true;

    _name = '';
    _visible = true;
    _tintClr = new Color(1, 1, 1);
    _transparency = 1.0;
    _useAlpha = false;
    _billboard = false;
    _videoPlaybackMode: VideoTexturePlaybackMode = 'per-frame';

    renderVisible: boolean = true;

    collisionMode: 'none' | 'block' = 'none';
    collisionShape: 'bounds' | 'primitive' = 'bounds';
    debugCollider: boolean = false;

    diffuseTexture: Texture = null;
    videoSource: VideoTextureSource | null = null;

    private _pendingVideoSource: VideoTextureSource | null = null;
    private _videoTextureLoadId = 0;
    private _restoringVideoTexture = false;
    private _renderEntityBaseRotation = new Quat();
    private _timelineFrameHandle?: { off: () => void };
    private _timelineTimeHandle?: { off: () => void };
    private _pendingTimelineVideoSync: Promise<void> | null = null;
    private _queuedTimelineVideoFrameTime: number | null = null;

    emissiveUnlitEnabled = true;

    private _shadowCatcher = false;

    private _onClipBoxChanged?: (clip: GlobalClipBox) => void;
    private _onClipBoxModeChanged?: (mode: 'shader' | 'test') => void;
    private _loadedEventFired = false;

    constructor(asset: Asset) {
        super(ElementType.model);

        this.asset = asset;

        try {
            const a: any = this.asset as any;
            a[ASSET_REFCOUNT_KEY] = (a[ASSET_REFCOUNT_KEY] ?? 0) + 1;
        } catch (e) {
            // ignore
        }
        this._name = ((asset.file as any)?.filename as string | undefined) || asset.name || 'GLTF Mesh';

        this.entity = new Entity('meshEntity');
        this.entity.setEulerAngles(0, 0, 0);
        this.renderEntity = new Entity('meshRenderEntity');
        this.entity.addChild(this.renderEntity);
        this._renderEntityBaseRotation.copy(this.renderEntity.getLocalRotation());

        Object.defineProperty(this.entity, 'model', {
            configurable: true,
            enumerable: true,
            get: () => this.renderEntity.model
        });

        this.selectionBoundStorage = new BoundingBox();
        this.localBoundStorage = new BoundingBox();
        this.worldBoundStorage = new BoundingBox();
    }

    set shadowCatcher(v: boolean) {
        const next = !!v;
        if (next === this._shadowCatcher) return;
        this._shadowCatcher = next;
        this.applyShadowCatcherSettings();
        if (this.diffuseTexture) {
            this.applyTexture();
        }
        this.changedCounter++;
        this.scene?.events?.fire('mesh.shadowCatcher', this);
    }

    get shadowCatcher() {
        return this._shadowCatcher;
    }

    get sourceMeshFile(): Blob | null {
        return ((this as any)._sourceMeshFile as Blob) ?? null;
    }

    get sourceMeshName(): string | null {
        const name = (this as any)._sourceMeshName;
        return typeof name === 'string' && name.length ? name : null;
    }

    async getSourceMeshArrayBuffer(): Promise<ArrayBuffer | null> {
        try {
            if (this.sourceMeshFile) {
                return await this.sourceMeshFile.arrayBuffer();
            }
        } catch (e) {
            // ignore
        }

        const url = (this.asset?.file as any)?.url as string | undefined;
        if (typeof url !== 'string' || !url) return null;

        try {
            const res = await fetch(url);
            if (!res.ok) return null;
            return await res.arrayBuffer();
        } catch (e) {
            return null;
        }
    }

    async add() {
        if (!this.scene) {
            console.error('[MESH] CRITICAL: Cannot add mesh - scene is null!');
            throw new Error('Mesh.add() called but scene is null');
        }

        if (!this.scene.contentRoot) {
            console.error('[MESH] CRITICAL: Cannot add mesh - scene.contentRoot is null!');
            throw new Error('Mesh.add() called but scene.contentRoot is null');
        }

        if (!this.asset) {
            console.error('[MESH] CRITICAL: Cannot add mesh - asset is null!');
            throw new Error('Mesh.add() called but asset is null');
        }

        this.scene.contentRoot.addChild(this.entity);

        try {
            if (!this.scene.app) {
                throw new Error('Scene app is null');
            }
            this.renderEntity.addComponent('model');
        } catch (e) {
            console.error('[MESH] CRITICAL: Error adding model component:', e);
            console.error('[MESH] Stack trace:', (e as Error).stack);
            this.scene.contentRoot.removeChild(this.entity);
            throw e;
        }

        if (!this.renderEntity.model) {
            const error = new Error('Model component is null after addComponent');
            console.error('[MESH] CRITICAL:', error.message);
            console.error('[MESH] Entity components:', Object.keys((this.renderEntity as any).c || {}));
            this.scene.contentRoot.removeChild(this.entity);
            throw error;
        }

        this.setupFromContainer();

        this.bindClipBoxEvents();
        this.bindTimelineEvents();

        await this.restoreVideoTextureFromMetadata();
    }

    private async restoreVideoTextureFromMetadata() {
        const sourceVideoFile = (this as any)._sourceVideoFile;
        if (!(sourceVideoFile instanceof File)) return;
        if (!this.scene?.app) return;
        if (this.videoSource || this._pendingVideoSource || this.diffuseTexture) return;
        if (this._restoringVideoTexture) return;

        this._restoringVideoTexture = true;

        try {
            await this.loadVideoTexture(sourceVideoFile);
        } finally {
            this._restoringVideoTexture = false;
        }
    }

    private setupFromContainer() {
        if (this.asset.resource) {
            const res: any = this.asset.resource as any;
            const assetAny = this.asset as any;

            if (res && res.model) {
                this.entity.model.type = 'asset';
                this.entity.model.asset = res.model;
            } else if (assetAny?.type === 'model' || (res && (res.meshes || res.graph))) {
                if (assetAny?.type === 'model' && this.asset?.registry) {
                    this.entity.model.type = 'asset';
                    this.entity.model.asset = this.asset as any;
                } else {
                    (this.entity.model as any).model = res;
                }
            } else {
                console.warn('No model found in asset resource');
                return;
            }

            this.configureMaterials();
            this.assignToWorldLayer();
            this.calculateBounds();
            this.countMeshInstances();
        }
    }

    private configureMaterials() {
        const checkModel = () => {
            if (this.entity.model && Array.isArray(this.entity.model.meshInstances) && this.entity.model.meshInstances.length > 0) {
                this.entity.model.meshInstances.forEach((meshInstance: MeshInstance) => {
                    if (meshInstance.material) {
                        meshInstance.material.depthTest = true;
                        meshInstance.material.depthWrite = true;

                        try {
                            const m = meshInstance.material as any;
                            if (m._originalUseLighting === undefined && typeof m.useLighting !== 'undefined') {
                                m._originalUseLighting = m.useLighting;
                            }
                            if (m._originalBlendType === undefined && typeof m.blendType !== 'undefined') {
                                m._originalBlendType = m.blendType;
                            }
                            if (m._originalDepthWrite === undefined && typeof m.depthWrite !== 'undefined') {
                                m._originalDepthWrite = m.depthWrite;
                            }

                            if (m.emissive && typeof m.emissive.set === 'function') {
                                m.emissive.set(1, 1, 1);
                            }
                            if (typeof m.emissiveIntensity === 'number') {
                                m.emissiveIntensity = 1;
                            }
                            if (!this._shadowCatcher && typeof m.useLighting !== 'undefined') {
                                m.useLighting = false;
                            }
                            if (m.diffuse && typeof m.diffuse.set === 'function') {
                                m.diffuse.set(1, 1, 1);
                            }
                            const pc: any = (window as any).pc;
                            if (typeof m.cull !== 'undefined') {
                                m.cull = pc?.CULLFACE_BACK ?? 1;
                            }
                        } catch (e) {}

                        const material = meshInstance.material as any;

                        if (!material._originalEmissive && material.emissive) {
                            material._originalEmissive = material.emissive.clone();
                        }
                        if (!material._originalDiffuse && material.diffuse) {
                            material._originalDiffuse = material.diffuse.clone();
                        }
                        if (material._originalOpacity === undefined) {
                            material._originalOpacity = material.opacity || 1.0;
                        }

                        meshInstance.material.update();
                    }
                });

                this.applyShadowCatcherSettings();
                this.applyClipBoxToMeshInstances();

                try {
                    this.calculateBounds();
                    if (this.diffuseTexture) {
                        this.applyTexture();
                    }
                    this.scene.boundDirty = true;
                    this.scene.events.fire('camera.focus');

                    if (!this._loadedEventFired) {
                        this._loadedEventFired = true;
                        this.scene.events.fire('mesh.loaded', this);
                    }
                } catch (e) {
                    console.warn('Post-ready setup failed:', e);
                }
            } else {
                setTimeout(checkModel, 16);
            }
        };

        checkModel();
    }

    private applyShadowCatcherSettings() {
        const instances = this.entity?.model?.meshInstances;
        if (!Array.isArray(instances) || instances.length === 0) return;

        for (const meshInstance of instances) {
            const miAny: any = meshInstance as any;
            miAny.castShadow = !this._shadowCatcher;
            miAny.receiveShadow = this._shadowCatcher;

            const material: any = meshInstance.material as any;
            if (!material) continue;

            if (material._originalUseLighting === undefined && typeof material.useLighting !== 'undefined') {
                material._originalUseLighting = material.useLighting;
            }
            if (material._originalBlendType === undefined && typeof material.blendType !== 'undefined') {
                material._originalBlendType = material.blendType;
            }
            if (material._originalDepthWrite === undefined && typeof material.depthWrite !== 'undefined') {
                material._originalDepthWrite = material.depthWrite;
            }

            if (this._shadowCatcher) {
                if (typeof material.useLighting !== 'undefined') material.useLighting = true;
                if (material.emissive && typeof material.emissive.set === 'function') material.emissive.set(0, 0, 0);
                if (typeof material.emissiveIntensity === 'number') material.emissiveIntensity = 0;
                if (material.diffuse && typeof material.diffuse.set === 'function') material.diffuse.set(1, 1, 1);
                if (typeof material.blendType !== 'undefined') material.blendType = BLEND_MULTIPLICATIVE;
                if (typeof material.depthWrite !== 'undefined') material.depthWrite = false;
            } else {
                if (typeof material.useLighting !== 'undefined' && material._originalUseLighting !== undefined) {
                    material.useLighting = material._originalUseLighting;
                }
                if (typeof material.blendType !== 'undefined' && material._originalBlendType !== undefined) {
                    material.blendType = material._originalBlendType;
                }
                if (typeof material.depthWrite !== 'undefined' && material._originalDepthWrite !== undefined) {
                    material.depthWrite = material._originalDepthWrite;
                }
            }

            try {
                material.update?.();
            } catch {
                // ignore
            }
        }
    }

    private bindClipBoxEvents() {
        if (!this.scene || this._onClipBoxChanged || this._onClipBoxModeChanged) return;

        this._onClipBoxChanged = () => {
            this.applyClipBoxToMeshInstances();
        };

        this._onClipBoxModeChanged = () => {
            this.applyClipBoxToMeshInstances();
        };

        this.scene.events.on('clipBox.changed', this._onClipBoxChanged);
        this.scene.events.on('clipBox.modeChanged', this._onClipBoxModeChanged);
    }

    private bindTimelineEvents() {
        if (!this.scene || this._timelineFrameHandle || this._timelineTimeHandle) return;

        this._timelineFrameHandle = this.scene.events.on('timeline.frame', (frame: number) => {
            if (this.scene?.events?.invoke('timeline.playing')) {
                return;
            }
            void this.queueVideoTimelineSync(frame);
        });

        this._timelineTimeHandle = this.scene.events.on('timeline.time', (time: number) => {
            void this.queueVideoTimelineSync(time);
        });
    }

    private unbindTimelineEvents() {
        this._timelineFrameHandle?.off();
        this._timelineFrameHandle = undefined;
        this._timelineTimeHandle?.off();
        this._timelineTimeHandle = undefined;
    }

    private applyClipBoxToMeshInstances() {
        if (!this.scene || !this.entity?.model?.meshInstances?.length) return;

        const clip = (this.scene.events.invoke('clipBox') ?? defaultGlobalClipBox()) as GlobalClipBox;
        const invRot = calcInvRotationMat3(clip.rotation);
        const enabled = clip.enabled ? 1 : 0;

        this.entity.model.meshInstances.forEach((meshInstance: MeshInstance) => {
            const material = meshInstance.material as StandardMaterial;
            if (!material) return;

            const matAny = material as any;
            if (!matAny._clipBoxChunkApplied) {
                const chunks = material.getShaderChunks?.(SHADERLANGUAGE_GLSL);
                if (!chunks) return;

                const prevDecl = chunks.get('litUserDeclarationPS') ?? '';
                const prevMainEnd = chunks.get('litUserMainEndPS') ?? '';
                chunks.set('litUserDeclarationPS', `${prevDecl}\n${clipBoxChunk}`);
                chunks.set('litUserMainEndPS', `${prevMainEnd}\nclipBoxDiscard(vPositionW.xyz);`);
                matAny._clipBoxChunkApplied = true;
            }

            meshInstance.setParameter('clipBoxEnabled', enabled);
            meshInstance.setParameter('clipBoxCenter', [clip.center.x, clip.center.y, clip.center.z]);
            meshInstance.setParameter('clipBoxHalf', [clip.halfExtents.x, clip.halfExtents.y, clip.halfExtents.z]);
            meshInstance.setParameter('clipBoxInvRot', invRot.data);

            material.update();
        });
    }

    private assignToWorldLayer() {
        const assignLayers = () => {
            if (this.entity.model && Array.isArray(this.entity.model.meshInstances) && this.entity.model.meshInstances.length > 0) {
                const worldLayer = this.scene.app.scene.layers.getLayerByName('World');
                if (worldLayer) {
                    this.entity.model.layers = [worldLayer.id];

                    this.entity.model.meshInstances.forEach((meshInstance, index) => {
                        meshInstance.drawOrder = -(1000 - index);

                        if (meshInstance.material) {
                            meshInstance.material.depthTest = true;
                            meshInstance.material.depthWrite = true;
                            meshInstance.material.alphaTest = 0;
                            meshInstance.material.update();
                        }
                    });
                } else {
                    console.warn('World layer not found, mesh may not render correctly with splats');
                }
            } else {
                setTimeout(assignLayers, 16);
            }
        };

        assignLayers();
    }

    private calculateBounds() {
        if (this.entity.model && Array.isArray(this.entity.model.meshInstances) && this.entity.model.meshInstances.length > 0) {
            let first = true;
            this.entity.model.meshInstances.forEach((meshInstance: MeshInstance, idx: number) => {
                const meshBoundWorld = meshInstance.aabb;
                if (!meshBoundWorld) {
                    console.warn('MeshInstance', idx, 'missing AABB');
                    return;
                }
                if (first) {
                    this.worldBoundStorage.copy(meshBoundWorld);
                    first = false;
                } else {
                    this.worldBoundStorage.add(meshBoundWorld);
                }
            });

            const invWorld = new Mat4();
            invWorld.copy(this.entity.getWorldTransform()).invert();
            this.localBoundStorage.setFromTransformedAabb(this.worldBoundStorage, invWorld);

            this.worldBoundDirty = false;
            this.localBoundDirty = false;
            this.selectionBoundDirty = true;
            if (this.scene) {
                this.scene.boundDirty = true;
            }
        }
    }

    private countMeshInstances() {
        if (this.entity.model && this.entity.model.meshInstances) {
            this.numMeshInstances = this.entity.model.meshInstances.length;
        }
    }

    remove() {
        if (this.scene && this.scene.contentRoot && this.entity) {
            this.scene.contentRoot.removeChild(this.entity);
            this.scene.boundDirty = true;
        }
    }

    destroy() {
        if (this.videoSource || this._pendingVideoSource) {
            this.clearVideoTexture();
        } else if (this.diffuseTexture) {
            this.diffuseTexture.destroy();
            this.diffuseTexture = null;
        }

        if (this.asset) {
            let shouldUnload = true;
            try {
                const a: any = this.asset as any;
                const next = (a[ASSET_REFCOUNT_KEY] ?? 1) - 1;
                a[ASSET_REFCOUNT_KEY] = next;
                if (next > 0) {
                    shouldUnload = false;
                } else {
                    delete a[ASSET_REFCOUNT_KEY];
                }
            } catch (e) {
                shouldUnload = true;
            }

            if (shouldUnload) {
                if (this.asset.registry) {
                    this.asset.registry.remove(this.asset);
                }
                this.asset.unload();
            }
        }

        super.destroy();

        if (this.scene && this._onClipBoxChanged) {
            this.scene.events.off('clipBox.changed', this._onClipBoxChanged);
            this._onClipBoxChanged = undefined;
        }
        if (this.scene && this._onClipBoxModeChanged) {
            this.scene.events.off('clipBox.modeChanged', this._onClipBoxModeChanged);
            this._onClipBoxModeChanged = undefined;
        }
        this.unbindTimelineEvents();

        if (this.entity) {
            this.entity.destroy();
        }
    }

    serialize(serializer: Serializer) {
        serializer.packa(this.entity.getWorldTransform().data);
        serializer.pack(this.changedCounter);
        serializer.pack(this.visible);
        serializer.pack(this.tintClr.r, this.tintClr.g, this.tintClr.b);
        serializer.pack(this.transparency);
        serializer.pack(this._shadowCatcher);
    }

    onUpdate(_deltaTime: number) {
        const billboardEnabled = this._billboard;
        if (!this.videoSource && !billboardEnabled) {
            return;
        }

        if (this.videoSource) {
            this.videoSource.update();
            if (this._videoPlaybackMode === 'realtime') {
                this.scene.forceRender = true;
            }
        }

        if (billboardEnabled) {
            this.applyBillboardRotation();
            this.scene.forceRender = true;
        } else {
            this.restoreRenderEntityRotation();
        }
    }

    onPreRender() {
        const events = this.scene.events;
        const selected = this.scene.camera.renderOverlays && events.invoke('selection') === this;

        if (this.entity.model && this.entity.model.meshInstances) {
            this.entity.model.meshInstances.forEach((meshInstance: MeshInstance) => {
                meshInstance.visible = this.renderVisible;

                const material = meshInstance.material as StandardMaterial;
                if (material) {
                    if (selected && !events.invoke('view.outlineSelection')) {
                        if (this.hasTexture && this.emissiveUnlitEnabled) {
                            if (material.emissive) {
                                material.emissive.set(1, 1, 1);
                            }
                            if (typeof (material as any).emissiveIntensity === 'number') {
                                (material as any).emissiveIntensity = 1;
                            }
                            if (!this._shadowCatcher && typeof (material as any).useLighting !== 'undefined') {
                                (material as any).useLighting = false;
                            }
                        } else {
                            const selectedClr = events.invoke('selectedClr');
                            if (material.emissive) {
                                material.emissive.set(
                                    selectedClr.r * 0.3,
                                    selectedClr.g * 0.3,
                                    selectedClr.b * 0.3
                                );
                            }
                        }
                    } else {
                        if (this.hasTexture && this.emissiveUnlitEnabled) {
                            if (material.emissive) {
                                material.emissive.set(1, 1, 1);
                            }
                            if (typeof (material as any).emissiveIntensity === 'number') {
                                (material as any).emissiveIntensity = 1;
                            }
                            if (!this._shadowCatcher && typeof (material as any).useLighting !== 'undefined') {
                                (material as any).useLighting = false;
                            }
                        } else {
                            const originalEmissive = (material as any)._originalEmissive;
                            if (material.emissive && originalEmissive) {
                                material.emissive.copy(originalEmissive);
                            }
                        }
                    }

                    const originalDiffuse = (material as any)._originalDiffuse;
                    if (material.diffuse && originalDiffuse) {
                        if (this.hasTexture) {
                            material.diffuse.set(1, 1, 1);
                            if (this.tintClr && !this.tintClr.equals(new Color(1, 1, 1))) {
                                material.diffuse.r *= this.tintClr.r;
                                material.diffuse.g *= this.tintClr.g;
                                material.diffuse.b *= this.tintClr.b;
                            }
                        } else {
                            material.diffuse.copy(originalDiffuse);
                            if (this.tintClr && !this.tintClr.equals(new Color(1, 1, 1))) {
                                material.diffuse.r *= this.tintClr.r;
                                material.diffuse.g *= this.tintClr.g;
                                material.diffuse.b *= this.tintClr.b;
                            }
                        }
                    }

                    const originalOpacity = (material as any)._originalOpacity || 1.0;
                    material.opacity = originalOpacity * this.transparency;
                    material.update();
                }
            });
        }

        if (this.visible && selected && events.invoke('camera.bound')) {
            const bound = this.localBound;
            const scale = new Mat4().setTRS(bound.center, Quat.IDENTITY, bound.halfExtents);
            scale.mul2(this.entity.getWorldTransform(), scale);

            for (let i = 0; i < boundingPoints.length / 2; i++) {
                const a = boundingPoints[i * 2];
                const b = boundingPoints[i * 2 + 1];
                scale.transformPoint(a, veca);
                scale.transformPoint(b, vecb);

                this.scene.app.drawLine(veca, vecb, Color.WHITE, true, this.scene.debugLayer);
            }
        }

        if (this.debugCollider) {
            const world = this.worldBound;
            if (world) {
                this.scene.app.drawWireAlignedBox(world.getMin(), world.getMax(), Color.YELLOW);
            }
        }

        this.entity.enabled = this.visible;
    }

    makeSelectionBoundDirty() {
        this.selectionBoundDirty = true;
        this.makeLocalBoundDirty();
    }

    makeLocalBoundDirty() {
        this.localBoundDirty = true;
        this.makeWorldBoundDirty();
    }

    makeWorldBoundDirty() {
        this.worldBoundDirty = true;
        this.scene.boundDirty = true;
    }

    get selectionBound() {
        if (this.selectionBoundDirty) {
            this.selectionBoundStorage.copy(this.localBound);
            this.selectionBoundDirty = false;
        }
        return this.selectionBoundStorage;
    }

    get localBound() {
        if (this.localBoundDirty) {
            this.calculateBounds();
            this.localBoundDirty = false;
        }
        return this.localBoundStorage;
    }

    get worldBound() {
        if (this.worldBoundDirty) {
            this.calculateBounds();
        }
        return this.worldBoundStorage;
    }

    focalPoint() {
        return this.worldBound.center.clone();
    }

    move(position?: Vec3, rotation?: Quat, scale?: Vec3) {
        const entity = this.entity;
        if (position) {
            entity.setLocalPosition(position);
        }
        if (rotation) {
            entity.setLocalRotation(rotation);
        }
        if (scale) {
            entity.setLocalScale(scale);
        }

        this.makeWorldBoundDirty();
        this.changedCounter++;
        this.scene.events.fire('mesh.moved', this);
    }

    getPivot(mode: 'center' | 'boundCenter', selection: boolean, result: Transform) {
        const { entity } = this;
        switch (mode) {
            case 'center':
                result.set(entity.getLocalPosition(), entity.getLocalRotation(), entity.getLocalScale());
                break;
            case 'boundCenter':
                entity.getLocalTransform().transformPoint((selection ? this.selectionBound : this.localBound).center, vec);
                result.set(vec, entity.getLocalRotation(), entity.getLocalScale());
                break;
        }
    }

    get worldTransform() {
        return this.entity.getWorldTransform();
    }

    set name(newName: string) {
        if (newName !== this.name) {
            this._name = newName;
            this.scene.events.fire('mesh.name', this);
        }
    }

    get name() {
        return this._name;
    }

    get filename() {
        return ((this.asset.file as any)?.filename as string | undefined) || this._name || 'GLTF Mesh';
    }

    set visible(value: boolean) {
        if (this.entity.enabled !== value) {
            this._visible = value;
            this.entity.enabled = value;
            this.scene.forceRender = true;
            this.scene.events.fire('mesh.visibility', this);
        }
    }

    get visible() {
        return this._visible;
    }

    set tintClr(value: Color) {
        if (!this._tintClr.equals(value)) {
            this._tintClr.copy(value);
            this.scene.events.fire('mesh.tintClr', this);
        }
    }

    get tintClr() {
        return this._tintClr;
    }

    set transparency(value: number) {
        if (value !== this._transparency) {
            this._transparency = value;
            this.scene.events.fire('mesh.transparency', this);
        }
    }

    get transparency() {
        return this._transparency;
    }

    set useAlpha(value: boolean) {
        const next = !!value;
        if (next === this._useAlpha) {
            return;
        }

        this._useAlpha = next;
        this.applyTexture();
        this.scene?.events?.fire('mesh.useAlpha', this);
    }

    get useAlpha() {
        return this._useAlpha;
    }

    set billboard(value: boolean) {
        const next = !!value;
        if (next === this._billboard) {
            return;
        }

        this._billboard = next;
        if (!next) {
            this.restoreRenderEntityRotation();
        } else {
            this.applyBillboardRotation();
        }
        if (this.scene) {
            this.scene.forceRender = true;
        }
        this.scene?.events?.fire('mesh.billboard', this);
    }

    get billboard() {
        return this._billboard;
    }

    set videoPlaybackMode(value: VideoTexturePlaybackMode) {
        const nextMode: VideoTexturePlaybackMode = value === 'per-frame' ? 'per-frame' : 'realtime';
        if (nextMode === this._videoPlaybackMode) {
            return;
        }

        this._videoPlaybackMode = nextMode;
        this.videoSource?.setPlaybackMode(nextMode);

        if (nextMode === 'per-frame') {
            void this.awaitVideoTimelineSync(this.getCurrentTimelineFrameTime());
        } else {
            this._queuedTimelineVideoFrameTime = null;
        }

        if (this.scene) {
            this.scene.forceRender = true;
        }
        this.scene?.events?.fire('mesh.videoPlaybackMode', this);
    }

    get videoPlaybackMode() {
        return this._videoPlaybackMode;
    }

    get videoLoop() {
        return this.videoSource?.loop ?? true;
    }

    set videoLoop(value: boolean) {
        this.videoSource?.setLoop(value);
        if (this.scene) {
            this.scene.forceRender = true;
        }
    }

    get videoPlaybackRate() {
        return this.videoSource?.playbackRate ?? 1;
    }

    set videoPlaybackRate(value: number) {
        this.videoSource?.setPlaybackRate(value);
        if (this.scene) {
            this.scene.forceRender = true;
        }
    }

    get videoPlaying() {
        return this.videoSource?.isPlaying ?? false;
    }

    playVideo() {
        void this.videoSource?.play();
        if (this.scene) {
            this.scene.forceRender = true;
        }
    }

    pauseVideo() {
        this.videoSource?.pause();
        if (this.scene) {
            this.scene.forceRender = true;
        }
    }

    private getTimelineFrameRate() {
        const frameRate = this.scene?.events?.invoke('timeline.frameRate');
        return typeof frameRate === 'number' && Number.isFinite(frameRate) && frameRate > 0 ? frameRate : 30;
    }

    private getCurrentTimelineFrameTime() {
        const frame = this.scene?.events?.invoke('timeline.frame');
        return typeof frame === 'number' && Number.isFinite(frame) ? frame : 0;
    }

    private queueVideoTimelineSync(frameTime: number) {
        if (!this.videoSource || this._videoPlaybackMode !== 'per-frame' || !this.scene) {
            return Promise.resolve();
        }

        this._queuedTimelineVideoFrameTime = Number.isFinite(frameTime) ? frameTime : 0;

        if (!this._pendingTimelineVideoSync) {
            this._pendingTimelineVideoSync = (async () => {
                while (this._queuedTimelineVideoFrameTime !== null && this.videoSource && this._videoPlaybackMode === 'per-frame') {
                    const targetFrameTime = this._queuedTimelineVideoFrameTime;
                    this._queuedTimelineVideoFrameTime = null;
                    await this.videoSource.syncToTimelineTime(targetFrameTime, this.getTimelineFrameRate());
                    this.scene.forceRender = true;
                }
            })().catch((error) => {
                console.warn('[Mesh] Failed to sync video texture to timeline time', error);
            }).finally(() => {
                this._pendingTimelineVideoSync = null;
                if (this._queuedTimelineVideoFrameTime !== null && this.videoSource && this._videoPlaybackMode === 'per-frame') {
                    void this.queueVideoTimelineSync(this._queuedTimelineVideoFrameTime);
                }
            });
        }

        return this._pendingTimelineVideoSync;
    }

    async awaitVideoTimelineSync(frameTime?: number) {
        if (!this.videoSource || this._videoPlaybackMode !== 'per-frame') {
            return;
        }

        const targetFrameTime = typeof frameTime === 'number' && Number.isFinite(frameTime) ? frameTime : this.getCurrentTimelineFrameTime();
        const frameRate = this.getTimelineFrameRate();
        await this.videoSource.syncToTimelineTime(targetFrameTime, frameRate, true);
        this.scene.forceRender = true;
    }

    loadTexture(file: File): Promise<void> {
        const MAX_TEXTURE_SIZE = 100 * 1024 * 1024;
        if (file.size > MAX_TEXTURE_SIZE) {
            return Promise.reject(new Error(`Texture file too large: ${(file.size / (1024 * 1024)).toFixed(1)}MB (max ${MAX_TEXTURE_SIZE / (1024 * 1024)}MB)`));
        }

        this._videoTextureLoadId++;
        if (this.videoSource || this._pendingVideoSource || this.diffuseTexture) {
            this.removeTexture();
        }

        return new Promise((resolve, reject) => {
            const url = URL.createObjectURL(file);
            const textureAsset = new Asset(file.name, 'texture', { url });

            this.scene.app.assets.add(textureAsset);

            textureAsset.on('load', () => {
                this.diffuseTexture = textureAsset.resource as Texture;
                try {
                    const gd = this.scene.app.graphicsDevice;
                    if (gd && this.diffuseTexture && typeof (this.diffuseTexture as any).anisotropy === 'number') {
                        (this.diffuseTexture as any).anisotropy = Math.min(gd.maxAnisotropy || 1, 16);
                    }
                } catch (e) {}
                if (this.diffuseTexture) {
                    (this.diffuseTexture as any).mipmaps = true;
                    (this.diffuseTexture as any).minFilter = ((window as any).pc?.FILTER_LINEAR_MIPMAP_LINEAR) ?? 5;
                    (this.diffuseTexture as any).magFilter = ((window as any).pc?.FILTER_LINEAR) ?? 1;
                }

                (this as any)._sourceTextureFile = file;
                (this as any)._sourceTextureName = file.name;
                (this as any)._sourceVideoFile = null;

                this.applyTexture();
                URL.revokeObjectURL(url);

                this.scene.events.fire('mesh.textureLoaded', this);
                resolve();
            });

            textureAsset.on('error', (err: any) => {
                console.error('Error loading texture asset:', err);
                URL.revokeObjectURL(url);
                reject(err);
            });

            this.scene.app.assets.load(textureAsset);
        });
    }

    private captureOriginalAlphaState(material: any) {
        if (!material) {
            return;
        }

        if (material._originalBlendType === undefined && typeof material.blendType !== 'undefined') {
            material._originalBlendType = material.blendType;
        }
        if (material._originalOpacityMap === undefined) {
            material._originalOpacityMap = material.opacityMap ?? null;
        }
        if (material._originalOpacityMapChannel === undefined && typeof material.opacityMapChannel !== 'undefined') {
            material._originalOpacityMapChannel = material.opacityMapChannel;
        }
        if (material._originalOpacityMapUv === undefined && typeof material.opacityMapUv === 'number') {
            material._originalOpacityMapUv = material.opacityMapUv;
        }
    }

    private applyMaterialAlphaState(material: any, uvIndex: number) {
        this.captureOriginalAlphaState(material);

        material.opacityMap = this.diffuseTexture;
        if (typeof material.opacityMapUv === 'number' && uvIndex >= 0) {
            material.opacityMapUv = uvIndex;
        }
        if (typeof material.opacityMapChannel !== 'undefined') {
            material.opacityMapChannel = 'a';
        }
        if (typeof material.blendType !== 'undefined') {
            material.blendType = BLEND_NORMAL;
        }
    }

    private restoreMaterialAlphaState(material: any, preserveBlendType = false) {
        if (!material) {
            return;
        }

        material.opacityMap = material._originalOpacityMap ?? null;

        if (typeof material.opacityMapChannel !== 'undefined' && material._originalOpacityMapChannel !== undefined) {
            material.opacityMapChannel = material._originalOpacityMapChannel;
        }

        if (typeof material.opacityMapUv === 'number' && material._originalOpacityMapUv !== undefined) {
            material.opacityMapUv = material._originalOpacityMapUv;
        }

        if (!preserveBlendType && typeof material.blendType !== 'undefined' && material._originalBlendType !== undefined) {
            material.blendType = material._originalBlendType;
        }
    }

    private getVideoTextureAlphaDefault(file: File) {
        const lowerName = file.name.toLowerCase();
        if (lowerName.endsWith('.webm')) {
            return true;
        }

        if (lowerName.endsWith('.mp4')) {
            return false;
        }

        return this._useAlpha;
    }

    private restoreRenderEntityRotation() {
        if (!this.renderEntity) {
            return;
        }

        this.renderEntity.setLocalRotation(this._renderEntityBaseRotation);
    }

    private applyBillboardRotation() {
        if (!this.renderEntity || !this.scene?.camera?.entity) {
            return;
        }

        billboardInverseWorld.copy(this.entity.getWorldTransform()).invert();
        billboardInverseWorld.transformPoint(this.scene.camera.entity.getPosition(), billboardLocalCamera);
        billboardLocalCamera.y = 0;

        const lengthSq = billboardLocalCamera.x * billboardLocalCamera.x + billboardLocalCamera.z * billboardLocalCamera.z;
        if (lengthSq <= 1e-8) {
            return;
        }

        const yaw = Math.atan2(billboardLocalCamera.x, billboardLocalCamera.z) * (180 / Math.PI);
        this.renderEntity.setLocalEulerAngles(0, yaw, 0);
    }

    async loadVideoTexture(file: File): Promise<void> {
        const loadId = ++this._videoTextureLoadId;
        const defaultUseAlpha = this.getVideoTextureAlphaDefault(file);
        const useCanvasSource = defaultUseAlpha;
        const useAlphaChanged = this._useAlpha !== defaultUseAlpha;

        this._useAlpha = defaultUseAlpha;

        if (this.videoSource || this._pendingVideoSource || this.diffuseTexture) {
            this.removeTexture();
        }

        const source = new VideoTextureSource(file, this.scene.app.graphicsDevice, {
            preserveAlpha: useCanvasSource
        });
        source.setPlaybackMode(this._videoPlaybackMode);
        this._pendingVideoSource = source;

        try {
            await source.ready;

            if (loadId !== this._videoTextureLoadId || this._pendingVideoSource !== source) {
                source.destroy();
                return;
            }

            this.diffuseTexture = source.texture;

            const applied = await this.waitForTextureApplication(source, loadId);
            if (!applied) {
                source.destroy();
                return;
            }

            if (loadId !== this._videoTextureLoadId || this._pendingVideoSource !== source) {
                source.destroy();
                return;
            }

            this._pendingVideoSource = null;
            this.videoSource = source;
            this.diffuseTexture = source.texture;
            (this as any)._sourceTextureFile = null;
            (this as any)._sourceTextureName = null;
            (this as any)._sourceVideoFile = file;

            this.scene.forceRender = true;
            this.scene.events.fire('mesh.textureLoaded', this);
            if (useAlphaChanged) {
                this.scene.events.fire('mesh.useAlpha', this);
            }
            if (this._videoPlaybackMode === 'per-frame') {
                await this.awaitVideoTimelineSync(this.getCurrentTimelineFrameTime());
            }
        } catch (error) {
            if (this._pendingVideoSource === source) {
                this._pendingVideoSource = null;
            }

            source.destroy();
            throw error;
        }
    }

    clearVideoTexture() {
        const pendingSource = this._pendingVideoSource;
        const activeSource = this.videoSource;

        if (!pendingSource && !activeSource) {
            return;
        }

        this._pendingVideoSource = null;
        this.videoSource = null;
        this._queuedTimelineVideoFrameTime = null;
        if (this.diffuseTexture === activeSource?.texture || this.diffuseTexture === pendingSource?.texture) {
            this.diffuseTexture = null;
        }

        (this as any)._sourceVideoFile = null;
        activeSource?.destroy();
        if (pendingSource && pendingSource !== activeSource) {
            pendingSource.destroy();
        }
        this.scene.forceRender = true;
    }

    private waitForTextureApplication(source: VideoTextureSource, loadId: number): Promise<boolean> {
        return new Promise((resolve) => {
            const check = () => {
                if (loadId !== this._videoTextureLoadId || this._pendingVideoSource !== source) {
                    resolve(false);
                    return;
                }

                if (this.diffuseTexture !== source.texture) {
                    this.diffuseTexture = source.texture;
                }

                const application = this.applyTexture();
                if (application.readyMeshInstanceCount > 0 &&
                    application.boundMeshInstanceCount === application.readyMeshInstanceCount) {
                    resolve(true);
                    return;
                }

                setTimeout(check, 16);
            };

            check();
        });
    }

    private applyTexture(): TextureApplicationResult {
        if (!this.diffuseTexture || !this.entity.model || !Array.isArray(this.entity.model.meshInstances) || this.entity.model.meshInstances.length === 0) {
            return {
                readyMeshInstanceCount: 0,
                boundMeshInstanceCount: 0
            };
        }

        let readyMeshInstanceCount = 0;
        let boundMeshInstanceCount = 0;

        this.entity.model.meshInstances.forEach((meshInstance: MeshInstance, index: number) => {
            if (meshInstance.material) {
                readyMeshInstanceCount++;
                const originalMaterial = meshInstance.material;

                let uvIndex = 0;
                if (meshInstance.mesh && meshInstance.mesh.vertexBuffer) {
                    const vb = meshInstance.mesh.vertexBuffer;
                    const hasUV0 = vb.format.elements.some((e: any) => e.name === 'TEXCOORD0');
                    const hasUV1 = vb.format.elements.some((e: any) => e.name === 'TEXCOORD1');
                    uvIndex = hasUV0 ? 0 : (hasUV1 ? 1 : -1);

                    if (uvIndex === -1) {
                        console.warn(`Mesh ${index} lacks UV coordinates - texture may not display properly`);
                    }
                }

                try {
                    if (!(originalMaterial as any)._isModifiedForTexture) {
                        (meshInstance as any)._originalMaterial = originalMaterial;
                        (originalMaterial as any)._originalDiffuseMap = (originalMaterial as any).diffuseMap || null;
                        (originalMaterial as any)._originalEmissiveMap = (originalMaterial as any).emissiveMap || null;
                    }

                    this.captureOriginalAlphaState(originalMaterial as any);

                    (originalMaterial as any).diffuseMap = this.diffuseTexture;
                    if (typeof (originalMaterial as any).diffuseMapUv === 'number' && uvIndex >= 0) {
                        (originalMaterial as any).diffuseMapUv = uvIndex;
                    }
                    (originalMaterial as any).emissiveMap = this.diffuseTexture;
                    if (typeof (originalMaterial as any).emissiveMapUv === 'number' && uvIndex >= 0) {
                        (originalMaterial as any).emissiveMapUv = uvIndex;
                    }
                    if ((originalMaterial as any).emissive && typeof (originalMaterial as any).emissive.set === 'function') {
                        (originalMaterial as any).emissive.set(1, 1, 1);
                    }
                    if (typeof (originalMaterial as any).emissiveIntensity === 'number') {
                        (originalMaterial as any).emissiveIntensity = 1;
                    }
                    if (this.emissiveUnlitEnabled && typeof (originalMaterial as any).useLighting !== 'undefined') {
                        (originalMaterial as any).useLighting = false;
                    }

                    if (this._shadowCatcher) {
                        this.restoreMaterialAlphaState(originalMaterial as any, true);
                    } else if (this._useAlpha) {
                        this.applyMaterialAlphaState(originalMaterial as any, uvIndex);
                    } else {
                        this.restoreMaterialAlphaState(originalMaterial as any);
                    }

                    (originalMaterial as any)._isModifiedForTexture = true;

                    if (typeof (originalMaterial as any).update === 'function') {
                        (originalMaterial as any).update();
                    } else {
                        (originalMaterial as any).shader = null;
                    }

                } catch (error) {
                    console.warn(`Failed to modify existing material ${index} directly:`, error);

                    const material = (originalMaterial as StandardMaterial).clone() as StandardMaterial;

                    material.diffuseMap = this.diffuseTexture;
                    if (typeof (material as any).diffuseMapUv === 'number' && uvIndex >= 0) {
                        (material as any).diffuseMapUv = uvIndex;
                    }
                    (material as any).emissiveMap = this.diffuseTexture;
                    if (typeof (material as any).emissiveMapUv === 'number' && uvIndex >= 0) {
                        (material as any).emissiveMapUv = uvIndex;
                    }
                    if ((material as any).emissive && typeof (material as any).emissive.set === 'function') {
                        (material as any).emissive.set(1, 1, 1);
                    }
                    if (typeof (material as any).emissiveIntensity === 'number') {
                        (material as any).emissiveIntensity = 1;
                    }
                    if (this.emissiveUnlitEnabled && typeof (material as any).useLighting !== 'undefined') {
                        (material as any).useLighting = false;
                    }

                    this.captureOriginalAlphaState(material as any);

                    if (this._shadowCatcher) {
                        this.restoreMaterialAlphaState(material as any, true);
                    } else if (this._useAlpha) {
                        this.applyMaterialAlphaState(material as any, uvIndex);
                    } else {
                        this.restoreMaterialAlphaState(material as any);
                    }

                    material.update();
                    (material as any)._isReplacementMaterial = true;
                    meshInstance.material = material;
                }

                const boundMaterial = meshInstance.material as any;
                if (boundMaterial?.diffuseMap === this.diffuseTexture && boundMaterial?.emissiveMap === this.diffuseTexture) {
                    boundMeshInstanceCount++;
                }
            }
        });

        this.scene.forceRender = true;
        this.scene.boundDirty = true;
        return {
            readyMeshInstanceCount,
            boundMeshInstanceCount
        };
    }

    removeTexture(): void {
        if (this.entity.model && this.entity.model.meshInstances) {
            this.entity.model.meshInstances.forEach((meshInstance: MeshInstance) => {
                const originalMaterial = (meshInstance as any)._originalMaterial;
                if (originalMaterial) {
                    meshInstance.material = originalMaterial;
                }

                const material = meshInstance.material as StandardMaterial;
                if (!material) {
                    return;
                }

                (material as any).diffuseMap = (material as any)._originalDiffuseMap ?? null;
                (material as any).emissiveMap = (material as any)._originalEmissiveMap ?? null;
                if (typeof (material as any).useLighting !== 'undefined' && (material as any)._originalUseLighting !== undefined) {
                    (material as any).useLighting = (material as any)._originalUseLighting;
                } else if (typeof (material as any).useLighting !== 'undefined') {
                    (material as any).useLighting = true;
                }
                if ((material as any).emissive && typeof (material as any).emissive.set === 'function') {
                    (material as any).emissive.set(0, 0, 0);
                }
                if (typeof (material as any).emissiveIntensity === 'number') {
                    (material as any).emissiveIntensity = 1;
                }
                this.restoreMaterialAlphaState(material as any);
                material.update();
            });
        }

        if (this.videoSource || this._pendingVideoSource) {
            this.clearVideoTexture();
        } else if (this.diffuseTexture) {
            this.diffuseTexture.destroy();
            this.diffuseTexture = null;
        }
        (this as any)._sourceTextureFile = null;
        (this as any)._sourceTextureName = null;
        (this as any)._sourceVideoFile = null;

        if (this._shadowCatcher) {
            this.applyShadowCatcherSettings();
        }

        this.scene.forceRender = true;
        this.scene.events.fire('mesh.textureRemoved', this);
    }

    get hasTexture(): boolean {
        return this.diffuseTexture !== null;
    }

    get numSplats() {
        return 0;
    }

    get numSelected() {
        return 0;
    }

    get numLocked() {
        return 0;
    }

    get numDeleted() {
        return 0;
    }

    docSerialize() {
        const pack3 = (v: Vec3) => [v.x, v.y, v.z];
        const pack4 = (q: Quat) => [q.x, q.y, q.z, q.w];
        const packC = (c: Color) => [c.r, c.g, c.b, c.a];

        return {
            name: this.name,
            position: pack3(this.entity.getLocalPosition()),
            rotation: pack4(this.entity.getLocalRotation()),
            scale: pack3(this.entity.getLocalScale()),
            visible: this.visible,
            tintClr: packC(this.tintClr),
            transparency: this.transparency,
            renderVisible: this.renderVisible,
            collisionMode: this.collisionMode,
            collisionShape: this.collisionShape,
            debugCollider: this.debugCollider
        };
    }

    docDeserialize(doc: any) {
        const { name, position, rotation, scale, visible, tintClr, transparency } = doc;

        this.name = name;
        this.move(new Vec3(position), new Quat(rotation), new Vec3(scale));
        this.visible = visible;
        this.tintClr = new Color(tintClr[0], tintClr[1], tintClr[2], tintClr[3]);
        this.transparency = transparency;

        if (doc.hasOwnProperty('renderVisible')) {
            this.renderVisible = !!doc.renderVisible;
        }
        if (doc.hasOwnProperty('collisionMode')) {
            this.collisionMode = (doc.collisionMode === 'block') ? 'block' : 'none';
        }
        if (doc.hasOwnProperty('collisionShape')) {
            this.collisionShape = (doc.collisionShape === 'primitive') ? 'primitive' : 'bounds';
        }
        if (doc.hasOwnProperty('debugCollider')) {
            this.debugCollider = !!doc.debugCollider;
        }
    }
}

export { Mesh };
